/**
 * Interface-element helpers: children lookup and creatable/editable roles from RBAC config.
 */

import type { InterfaceElement } from '@/types/interface-elements.types';
import { findRoleByCode, formatRoleByMode, type RoleCodeMatch } from '@/utils/role-label-utils';

/** Children of a parent by `parentId` UUID (parent may live in another interface). */
export function getChildrenByParentId(
  elements: InterfaceElement[],
  parentId: string,
): InterfaceElement[] {
  return elements.filter((el) => el.parentId === parentId);
}

/**
 * Children of a parent by `elementId`.
 * If the parent is missing from `adminElements` (e.g. it lives on the sidebar), returns [].
 */
export function getChildren(
  adminElements: InterfaceElement[],
  parentElementId: string,
): InterfaceElement[] {
  const parent = adminElements.find((el) => el.elementId === parentElementId);

  if (parent) {
    return adminElements.filter((el) => el.parentId === parent.id);
  }

  return [];
}

/** Action config bag for create/edit-role buttons. */
type RoleActionConfig = {
  subtype?: string;
  targetRole?: string;
};

/** Reads the action config without widening the element to `any`. */
function roleActionConfig(element: InterfaceElement): RoleActionConfig {
  return (element.config ?? {}) as RoleActionConfig;
}

/** Editable roles from `config.subtype === 'edit-role'` (admins / volunteers / tutors). */
export function getEditableRoles(
  elements: InterfaceElement[],
): Array<{ value: string; label: string }> {
  return elements
    .filter((el) => el.elementType === 'action' && roleActionConfig(el).subtype === 'edit-role')
    .map((el) => ({
      value: roleActionConfig(el).targetRole || '',
      label: el.elementLabel,
    }));
}

/** Creatable roles from `config.subtype === 'create-role'` (admins interface). */
export function getCreatableRoles(
  adminElements: InterfaceElement[],
): Array<{ value: string; label: string }> {
  return adminElements
    .filter((el) => el.elementType === 'action' && roleActionConfig(el).subtype === 'create-role')
    .map((el) => ({
      value: roleActionConfig(el).targetRole || '',
      label: el.elementLabel,
    }));
}

/** Creatable roles from tabs of a menu (tutors, volunteers, … — tabs = managed roles). */
export function getCreatableRolesFromTabs(
  elements: InterfaceElement[],
): Array<{ value: string; label: string }> {
  return elements
    .filter((el) => el.elementType === 'tab')
    .sort((a, b) => (a.userOrder ?? a.order ?? Infinity) - (b.userOrder ?? b.order ?? Infinity))
    .map((el) => ({
      value: el.elementId.replace('tab-', ''),
      label: el.elementLabel,
    }));
}

/**
 * Only roles actually returned by GET /roles are included.
 * A role listed in interface_elements but missing from the API is omitted
 * so the UI never shows a phantom option whose value is a raw code.
 */
export function buildCreatableRoleOptions(
  creatableRoles: Array<{ value: string; label: string }>,
  roles: RoleCodeMatch[],
): Array<{ value: string; label: string }> {
  const resolved = creatableRoles.flatMap((creatable) => {
    const fullRole = findRoleByCode(roles, creatable.value);
    if (!fullRole) return [];
    return [{ value: fullRole.id, label: fullRole.label, code: fullRole.code }];
  });
  return formatRoleByMode(resolved);
}
